import { useEffect, useMemo, useRef, useState } from "react";
import styles from "./Designer.module.css";
import {
    MASTER_PRINT_FONT_BASE_URL,
    getFontFaceUrl,
    sanitizeColorName,
} from "../../utils/designerUtils";
import {
    MAX_PRINT_IMAGE_FILE_SIZE,
    PRINT_IMAGE_ACCEPT,
    isSupportedPrintImageFile,
    readBlobAsDataUrl,
    verifyPredesign,
    PredesignTintPreview,
} from "./partsSectionPredesign";
import {
    getPredesignSizeLabel,
    normalizePredesignSizeTier,
    PREDESIGN_SIZE_TIERS,
} from "../../utils/designerHelpers";

const injectedFonts = new Set();
// Acepta URL completa (file_url del ABM) o file relativo contra baseUrl (/fonts/master, tabla fija).
const ensureFontFace = (family, fileOrUrl, baseUrl = MASTER_PRINT_FONT_BASE_URL) => {
    if (!family || !fileOrUrl || typeof document === "undefined") return;
    const id = `font-${family}`;
    if (injectedFonts.has(family) || document.getElementById(id)) return;
    const src = String(fileOrUrl).startsWith("http") ? String(fileOrUrl) : `${baseUrl}${fileOrUrl}`;
    const format = /\.otf(\?|$)/i.test(src) ? "opentype" : "truetype";
    const style = document.createElement("style");
    style.id = id;
    style.innerHTML = `
        @font-face {
            font-family: '${family}';
            src: url('${src}') format('${format}');
            font-weight: normal;
            font-style: normal;
        }
    `;
    document.head.appendChild(style);
    injectedFonts.add(family);
};

const SectionTitle = ({ title, subtitle }) => (
    <div className={styles.wizardSectionHeader}>
        <h4 className={styles.wizardSectionTitle}>{title}</h4>
        {subtitle ? <p className={styles.wizardSectionSubtitle}>{subtitle}</p> : null}
    </div>
);

const PRINT_COLOR_MODAL_PREVIEW_LIMIT = 6;

export const PrintColorQuickSetupModal = ({
    title = "Elegí los colores",
    subtitle = "Seleccioná los colores del diseño.",
    allColors = [],
    colorSlots = 1,
    initialColorIds = [],
    applying = false,
    onClose,
    onApply,
}) => {
    const normalizedSlots = Math.max(1, Number(colorSlots) || 1);
    const initialColorIdsKey = useMemo(
        () => Array.from({ length: normalizedSlots }).map((_, index) => String(initialColorIds?.[index] || "")).join("|"),
        [initialColorIds, normalizedSlots]
    );
    const [selectedColorIds, setSelectedColorIds] = useState(
        Array.from({ length: normalizedSlots }).map((_, index) => String(initialColorIds?.[index] || ""))
    );
    const [expandedSlots, setExpandedSlots] = useState({});
    const autoAppliedRef = useRef(false);

    useEffect(() => {
        setSelectedColorIds(
            Array.from({ length: normalizedSlots }).map((_, index) => String(initialColorIds?.[index] || ""))
        );
        setExpandedSlots({});
        autoAppliedRef.current = false;
    }, [initialColorIdsKey, normalizedSlots]);

    useEffect(() => {
        if (applying) return;
        if (autoAppliedRef.current) return;
        const allSlotsSelected = (selectedColorIds || []).every((id) => String(id || "").trim().length > 0);
        if (!allSlotsSelected) return;
        autoAppliedRef.current = true;
        onApply?.((selectedColorIds || []).map((id) => String(id || "")));
    }, [applying, onApply, selectedColorIds]);

    return (
        <div className={styles.confirmOverlay} onClick={() => (applying ? null : onClose?.())}>
            <div
                className={styles.partOptionModalCard}
                role="dialog"
                aria-modal="true"
                aria-label="Configuración de colores del diseño"
                onClick={(event) => event.stopPropagation()}
            >
                <section className={styles.sectionCard}>
                    <SectionTitle title={title} subtitle={subtitle} />

                    {Array.from({ length: normalizedSlots }).map((_, index) => {
                        const slot = index + 1;
                        const selectedColorId = String(selectedColorIds[index] || "");
                        const expanded = !!expandedSlots[slot];
                        const visibleColors = expanded
                            ? allColors
                            : allColors.slice(0, PRINT_COLOR_MODAL_PREVIEW_LIMIT);
                        const hiddenCount = Math.max(0, allColors.length - PRINT_COLOR_MODAL_PREVIEW_LIMIT);
                        const selectedColor = (allColors || []).find((color) => String(color?.id || "") === selectedColorId) || null;

                        return (
                            <div key={`print-color-slot-${slot}`} style={{ marginBottom: 14 }}>
                                <div className={styles.inputLabel}>{`Color ${slot}`}</div>
                                <div className={styles.colorRow}>
                                    {visibleColors.map((color) => {
                                        const selected = String(color.id) === selectedColorId;
                                        return (
                                            <button
                                                key={`print-slot-${slot}-color-${color.id}`}
                                                type="button"
                                                className={`${styles.colorDot} ${selected ? styles.selected : ""}`}
                                                style={{ background: color.code || "#d8d8d8" }}
                                                onClick={() => {
                                                    setSelectedColorIds((prev) => {
                                                        const next = [...prev];
                                                        next[index] = String(color.id);
                                                        return next;
                                                    });
                                                }}
                                                title={`${sanitizeColorName(color.name, "Color")} (${color.code || "sin código"})`}
                                                disabled={applying}
                                            />
                                        );
                                    })}
                                    {!expanded && hiddenCount > 0 ? (
                                        <span className={styles.paletteCollapsedHint}>{`+${hiddenCount}`}</span>
                                    ) : null}
                                </div>
                                <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", gap: 10 }}>
                                    {allColors.length > PRINT_COLOR_MODAL_PREVIEW_LIMIT ? (
                                        <button
                                            type="button"
                                            className={styles.paletteChevronBtn}
                                            onClick={() => {
                                                setExpandedSlots((prev) => ({ ...prev, [slot]: !prev[slot] }));
                                            }}
                                            disabled={applying}
                                            aria-expanded={expanded ? "true" : "false"}
                                            title={expanded ? "Contraer paleta" : "Expandir paleta"}
                                        >
                                            {expanded ? "⌃" : "⌄"}
                                        </button>
                                    ) : <span />}
                                    <button
                                        type="button"
                                        className={`${styles.btn} ${styles.viewBtn}`}
                                        disabled={applying}
                                        onClick={() => {
                                            setSelectedColorIds((prev) => {
                                                const next = [...prev];
                                                next[index] = "";
                                                return next;
                                            });
                                            autoAppliedRef.current = false;
                                        }}
                                    >
                                        {selectedColor ? `Quitar ${sanitizeColorName(selectedColor.name, "color")}` : `Quitar color ${slot}`}
                                    </button>
                                </div>
                            </div>
                        );
                    })}

                    <div className={styles.modalFooter}>
                        <button type="button" className={styles.btn} onClick={onClose} disabled={applying}>
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className={`${styles.btn} ${styles.nextBtn}`}
                            disabled={applying}
                            onClick={() => onApply?.(selectedColorIds.map((id) => String(id || "")))}
                        >
                            Aplicar
                        </button>
                    </div>
                </section>
            </div>
        </div>
    );
};

const hexToRgb = (hex) => {
    const raw = String(hex || "").trim().replace("#", "");
    if (!raw) return null;
    const normalized = raw.length === 3
        ? raw.split("").map((char) => `${char}${char}`).join("")
        : raw;
    if (!/^[0-9a-fA-F]{6}$/.test(normalized)) return null;
    const intVal = Number.parseInt(normalized, 16);
    return {
        r: (intVal >> 16) & 255,
        g: (intVal >> 8) & 255,
        b: intVal & 255,
    };
};

const findDefaultPredesignColorId = (colors = []) => {
    const items = Array.isArray(colors) ? colors : [];
    if (!items.length) return "";

    const littleBlue = items.find((color) => {
        const code = String(color?.code || "").toLowerCase();
        const name = String(color?.name || "").toLowerCase();
        return (
            code === "#0071ce"
            || code === "#006fcc"
            || name.includes("little blue")
            || name.includes("azul")
        );
    });
    if (littleBlue?.id !== undefined && littleBlue?.id !== null) return String(littleBlue.id);

    const dark = items.find((color) => {
        const rgb = hexToRgb(color?.code);
        if (!rgb) return false;
        const luminance = (0.2126 * rgb.r) + (0.7152 * rgb.g) + (0.0722 * rgb.b);
        return luminance < 120;
    });
    if (dark?.id !== undefined && dark?.id !== null) return String(dark.id);

    const first = items[0];
    return first?.id !== undefined && first?.id !== null ? String(first.id) : "";
};

const renderLittleBlueBold = (text = "") => {
    const normalized = String(text || "").replace(/littleblue/gi, "Little Blue");
    const parts = normalized.split(/(Little Blue)/gi);
    return parts.map((part, index) => (
        /little blue/i.test(part)
            ? <strong key={`lb-${index}`}>Little Blue</strong>
            : <span key={`txt-${index}`}>{part}</span>
    ));
};

let removeBackgroundModulePromise = null;
const loadRemoveBackground = async () => {
    if (!removeBackgroundModulePromise) {
        removeBackgroundModulePromise = import("@imgly/background-removal");
    }
    const module = await removeBackgroundModulePromise;
    return module?.removeBackground;
};

export const LittleDesignsModal = ({
    designs = [],
    loading = false,
    onClose,
    onSelectDesign,
}) => {
    const [verifiedDesignIds, setVerifiedDesignIds] = useState(new Set());
    const [verifying, setVerifying] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const run = async () => {
            if (loading) return;
            if (!designs.length) {
                setVerifiedDesignIds(new Set());
                return;
            }
            setVerifying(true);
            const results = await Promise.all(
                designs.map(async (design) => ({
                    id: String(design?.id || ""),
                    ok: await verifyPredesign(design),
                }))
            );
            if (cancelled) return;
            const next = new Set(
                results
                    .filter((item) => item.ok && item.id)
                    .map((item) => item.id)
            );
            setVerifiedDesignIds(next);
            setVerifying(false);
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [designs, loading]);

    const visibleDesigns = useMemo(
        () => designs.filter((design) => verifiedDesignIds.has(String(design?.id || ""))),
        [designs, verifiedDesignIds]
    );

    return (
        <div className={styles.confirmOverlay} onClick={onClose}>
            <div
                className={styles.partOptionModalCard}
                role="dialog"
                aria-modal="true"
                aria-label="Diseños Little"
                onClick={(event) => event.stopPropagation()}
            >
                <section className={styles.sectionCard}>
                    <SectionTitle
                        title="Diseños Little"
                        subtitle="Elegí un prediseño para insertarlo en la prenda."
                    />
                    {loading ? (
                        <div className={styles.noPrintsMessage}>Cargando diseños...</div>
                    ) : null}
                    {!loading && verifying ? (
                        <div className={styles.noPrintsMessage}>Validando imágenes disponibles...</div>
                    ) : null}
                    {!loading && !verifying && visibleDesigns.length === 0 ? (
                        <div className={styles.noPrintsMessage}>No hay prediseños disponibles por el momento.</div>
                    ) : null}
                    {!loading && !verifying && visibleDesigns.length > 0 ? (
                        <div className={styles.littleDesignsGrid}>
                            {visibleDesigns.map((design) => {
                                const preview = design?.img || design?.icon_url || design?.img_url || null;
                                const title = design?.factory_name || design?.nice_name || design?.name || `Diseño ${design?.id}`;
                                return (
                                    <button
                                        key={`little-design-${design.id}`}
                                        type="button"
                                        className={styles.littleDesignCard}
                                        onClick={() => onSelectDesign?.(design)}
                                    >
                                        <div className={styles.littleDesignThumbWrap}>
                                            {preview ? (
                                                <img
                                                    src={preview}
                                                    alt={title}
                                                    className={styles.littleDesignThumb}
                                                />
                                            ) : (
                                                <span className={styles.littleDesignThumbEmpty}>Sin preview</span>
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    ) : null}
                    <div className={styles.modalFooter}>
                        <button type="button" className={styles.btn} onClick={onClose}>
                            Cerrar
                        </button>
                    </div>
                </section>
            </div>
        </div>
    );
};

export const LittlePredesignEditorModal = ({
    title = "Color del prediseño",
    subtitle = "Ajustá los colores y aplicalo al diseño.",
    imageSrc = "",
    allColors = [],
    colorSlots = 1,
    initialColorIds = [],
    initialSizeTier = PREDESIGN_SIZE_TIERS.MEDIUM,
    techniqueKey = "bordado",
    applying = false,
    onClose,
    onApply,
}) => {
    const normalizedSlots = Math.max(1, Number(colorSlots) || 1);
    const initialColorIdsKey = useMemo(
        () => Array.from({ length: normalizedSlots }).map((_, index) => String(initialColorIds[index] || "")).join("|"),
        [initialColorIds, normalizedSlots]
    );
    const defaultPredesignColorId = useMemo(
        () => findDefaultPredesignColorId(allColors),
        [allColors]
    );
    const [selectedColorIds, setSelectedColorIds] = useState(
        Array.from({ length: normalizedSlots }).map((_, index) => {
            const incoming = String(initialColorIds[index] || "");
            if (incoming) return incoming;
            return index === 0 ? defaultPredesignColorId : "";
        })
    );
    const [selectedSizeTier, setSelectedSizeTier] = useState(
        normalizePredesignSizeTier(initialSizeTier)
    );
    const [step, setStep] = useState("colors");
    const colorById = useMemo(
        () => new Map((allColors || []).map((color) => [String(color?.id || ""), color]).filter((entry) => entry[0])),
        [allColors]
    );
    const previewColorHexes = useMemo(
        () => selectedColorIds
            .map((id) => colorById.get(String(id || ""))?.code)
            .filter(Boolean),
        [selectedColorIds, colorById]
    );
    const isEmbroidery = String(techniqueKey || "").toLowerCase() === "bordado";

    useEffect(() => {
        setSelectedColorIds(
            Array.from({ length: normalizedSlots }).map((_, index) => {
                const incoming = String(initialColorIds[index] || "");
                if (incoming) return incoming;
                return index === 0 ? defaultPredesignColorId : "";
            })
        );
        setSelectedSizeTier(normalizePredesignSizeTier(initialSizeTier));
        setStep("colors");
    }, [initialColorIdsKey, normalizedSlots, defaultPredesignColorId, initialSizeTier]);
    const primaryColorId = String(selectedColorIds[0] || defaultPredesignColorId || "").trim();
    const canAdvanceToSize = primaryColorId.length > 0;
    const resolvedColorIds = selectedColorIds.map((id, index) => {
        const normalized = String(id || "").trim();
        if (normalized) return normalized;
        return index === 0 ? primaryColorId : (primaryColorId || normalized);
    });
    const sizeOptions = [
        PREDESIGN_SIZE_TIERS.SMALL,
        PREDESIGN_SIZE_TIERS.MEDIUM,
        PREDESIGN_SIZE_TIERS.LARGE,
    ];

    return (
        <div className={styles.confirmOverlay} onClick={() => (applying ? null : onClose?.())}>
            <div
                className={styles.partOptionModalCard}
                role="dialog"
                aria-modal="true"
                aria-label="Editar prediseño"
                onClick={(event) => event.stopPropagation()}
            >
                <section className={styles.sectionCard}>
                    <SectionTitle title={title} subtitle={subtitle} />
                    <div className={styles.segmented} style={{ marginBottom: 12 }}>
                        <button
                            type="button"
                            className={step === "colors" ? styles.active : ""}
                            onClick={() => setStep("colors")}
                            disabled={applying}
                        >
                            1. Colores
                        </button>
                        <button
                            type="button"
                            className={step === "size" ? styles.active : ""}
                            onClick={() => {
                                if (!canAdvanceToSize || applying) return;
                                setStep("size");
                            }}
                            disabled={applying || !canAdvanceToSize}
                        >
                            2. Tamaño
                        </button>
                    </div>
                    <div className={styles.littleDesignEditorPreviewWrap}>
                        {imageSrc ? (
                            <PredesignTintPreview
                                src={imageSrc}
                                colorHex={previewColorHexes[0] || null}
                                colorHexes={previewColorHexes}
                                isEmbroidery={isEmbroidery}
                            />
                        ) : (
                            <div className={styles.noPrintsMessage}>No se encontró una preview para este prediseño.</div>
                        )}
                    </div>
                    {step === "colors" ? (
                        Array.from({ length: normalizedSlots }).map((_, index) => {
                            const slot = index + 1;
                            const selectedColorId = String(selectedColorIds[index] || "");
                            return (
                                <div key={`predesign-color-slot-${slot}`} style={{ marginBottom: 10 }}>
                                    <div className={styles.inputLabel}>{`Color ${slot}`}</div>
                                    <div className={styles.colorRow}>
                                        {allColors.map((color) => {
                                            const selected = String(color.id) === selectedColorId;
                                            return (
                                                <button
                                                    key={`predesign-slot-${slot}-color-${color.id}`}
                                                    type="button"
                                                    className={`${styles.colorDot} ${selected ? styles.selected : ""}`}
                                                    style={{ background: color.code || "#d8d8d8" }}
                                                    onClick={() => {
                                                        setSelectedColorIds((prev) => {
                                                            const next = [...prev];
                                                            next[index] = String(color.id);
                                                            return next;
                                                        });
                                                    }}
                                                    title={`${sanitizeColorName(color.name, "Color")} (${color.code || "sin código"})`}
                                                />
                                            );
                                        })}
                                    </div>
                                    <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end" }}>
                                        <button
                                            type="button"
                                            className={`${styles.btn} ${styles.viewBtn}`}
                                            disabled={applying}
                                            onClick={() => {
                                                setSelectedColorIds((prev) => {
                                                    const next = [...prev];
                                                    next[index] = "";
                                                    return next;
                                                });
                                            }}
                                        >
                                            {`Quitar color ${slot}`}
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <div>
                            <div className={styles.inputLabel}>Tamaño del prediseño</div>
                            <div className={styles.pillGroup}>
                                {sizeOptions.map((tier) => (
                                    <button
                                        key={`predesign-size-${tier}`}
                                        type="button"
                                        className={`${styles.pill} ${selectedSizeTier === tier ? styles.pillActive : ""}`}
                                        onClick={() => setSelectedSizeTier(tier)}
                                        disabled={applying}
                                    >
                                        {getPredesignSizeLabel(tier)}
                                    </button>
                                ))}
                            </div>
                            <div className={styles.hint} style={{ marginTop: 10 }}>
                                Elegí entre pequeño, mediano o grande para aplicarlo al diseño.
                            </div>
                        </div>
                    )}
                    <div className={styles.modalFooter}>
                        <button type="button" className={styles.btn} onClick={onClose} disabled={applying}>
                            Cancelar
                        </button>
                        {step === "colors" ? (
                            <button
                                type="button"
                                className={`${styles.btn} ${styles.nextBtn}`}
                                disabled={applying || !canAdvanceToSize}
                                onClick={() => setStep("size")}
                            >
                                Continuar
                            </button>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    className={`${styles.btn} ${styles.viewBtn}`}
                                    disabled={applying}
                                    onClick={() => setStep("colors")}
                                >
                                    Volver
                                </button>
                                <button
                                    type="button"
                                    className={`${styles.btn} ${styles.nextBtn}`}
                                    disabled={applying}
                                    onClick={() => onApply?.({
                                        colorIds: resolvedColorIds,
                                        sizeTier: selectedSizeTier,
                                    })}
                                >
                                    Aplicar
                                </button>
                            </>
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
};

export const PrintTextQuickSetupModal = ({
    title = "Configurá tu texto",
    subtitle = "Definí el contenido inicial y la tipografía del diseño.",
    textValue = "Apodo",
    maxLetters = 0,
    fonts = [],
    fontId = null,
    personal = "0",
    fontBaseUrl = MASTER_PRINT_FONT_BASE_URL,
    onClose,
    onApply,
}) => {
    const [text, setText] = useState(String(textValue || "Apodo"));
    const [selectedFontId, setSelectedFontId] = useState(fontId ? String(fontId) : "");
    const [openFontFor, setOpenFontFor] = useState(false);
    const isPersonal = String(personal || "0") === "1";
    const safeMax = Number(maxLetters || 0);
    const availableFonts = useMemo(
        () => (fonts || []).filter((font) => font?.file),
        [fonts]
    );
    const selectedFont = availableFonts.find((font) => String(font.id) === String(selectedFontId || ""));

    availableFonts.forEach((font) => ensureFontFace(font.family, getFontFaceUrl(font), fontBaseUrl));

    useEffect(() => {
        setText(String(textValue || "Apodo"));
    }, [textValue]);
    useEffect(() => {
        setSelectedFontId(fontId ? String(fontId) : "");
    }, [fontId]);

    return (
        <div className={styles.confirmOverlay} onClick={onClose}>
            <div
                className={styles.partOptionModalCard}
                role="dialog"
                aria-modal="true"
                aria-label="Configuración inicial de texto"
                onClick={(event) => event.stopPropagation()}
            >
                <section className={styles.sectionCard}>
                    <SectionTitle title={title} subtitle={subtitle} />
                    <div className={styles.inputGroup}>
                        <label className={styles.inputLabel}>Texto</label>
                        <textarea
                            rows={2}
                            className={styles.inputField}
                            placeholder="Apodo"
                            value={text}
                            maxLength={safeMax > 0 ? safeMax : 999}
                            onChange={(event) => setText(event.target.value)}
                            disabled={isPersonal}
                            style={{
                                minHeight: 74,
                                fontFamily: selectedFont ? `'${selectedFont.family}', sans-serif` : undefined,
                            }}
                        />
                        {safeMax > 0 ? (
                            <div className={styles.charCounter}>
                                {String(text || "").length} / {safeMax}
                            </div>
                        ) : null}
                    </div>
                    <div className={styles.inputGroup}>
                        <label className={styles.inputLabel}>Tipografía</label>
                        <button
                            type="button"
                            className={styles.selectField}
                            onClick={() => setOpenFontFor((prev) => !prev)}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                fontFamily: selectedFont ? `'${selectedFont.family}', sans-serif` : undefined,
                            }}
                        >
                            <span>{selectedFont?.name || "Predeterminada"}</span>
                            <span>▾</span>
                        </button>
                        {openFontFor ? (
                            <div className={styles.fontDropdown}>
                                <button
                                    type="button"
                                    className={`${styles.fontOption} ${!selectedFontId ? styles.fontOptionActive : ""}`}
                                    onClick={() => {
                                        setSelectedFontId("");
                                        setOpenFontFor(false);
                                    }}
                                >
                                    Predeterminada
                                </button>
                                {availableFonts.map((font) => (
                                    <button
                                        key={`quick-font-${font.id}`}
                                        type="button"
                                        className={`${styles.fontOption} ${String(font.id) === String(selectedFontId || "") ? styles.fontOptionActive : ""}`}
                                        style={{ fontFamily: `'${font.family}', sans-serif` }}
                                        onClick={() => {
                                            setSelectedFontId(String(font.id));
                                            setOpenFontFor(false);
                                        }}
                                    >
                                        {font.name}
                                    </button>
                                ))}
                            </div>
                        ) : null}
                    </div>
                    <div className={styles.modalFooter}>
                        <button type="button" className={styles.btn} onClick={onClose}>
                            Cerrar
                        </button>
                        <button
                            type="button"
                            className={`${styles.btn} ${styles.nextBtn}`}
                            onClick={() => onApply?.({
                                text: isPersonal ? "Apodo" : (String(text || "").trim() || "Apodo"),
                                fontId: selectedFontId || null,
                                personal: isPersonal ? "1" : "0",
                            })}
                        >
                            Aplicar
                        </button>
                    </div>
                </section>
            </div>
        </div>
    );
};

export const PrintImageQuickSetupModal = ({
    title = "Configuración de Estampado: Imagen personalizada",
    subtitle = "Cargá la imagen y aplicala al diseño activo.",
    legalImageDisclaimer = "",
    applying = false,
    onClose,
    onApply,
}) => {
    const [urlValue, setUrlValue] = useState("");
    const [fileName, setFileName] = useState("");
    const [fileError, setFileError] = useState("");
    const [isRemovingBackground, setIsRemovingBackground] = useState(false);
    const [processedImageDataUrl, setProcessedImageDataUrl] = useState("");
    const previewSrc = String(processedImageDataUrl || urlValue || "").trim();
    const canApply = Boolean(String(urlValue || "").trim() || processedImageDataUrl) && !isRemovingBackground && !applying;

    return (
        <div className={styles.confirmOverlay} onClick={() => (applying || isRemovingBackground ? null : onClose?.())}>
            <div
                className={styles.partOptionModalCard}
                role="dialog"
                aria-modal="true"
                aria-label="Configuración inicial de imagen personalizada"
                onClick={(event) => event.stopPropagation()}
            >
                <section className={styles.sectionCard}>
                    <SectionTitle title={title} subtitle={subtitle} />

                    <div className={`${styles.summaryBox} mb-3`}>
                        <div className={styles.summaryTitle}>Resumen del diseño activo</div>
                        <div className={styles.summaryList}>
                            <div className={styles.summaryItem}><strong>Tipo:</strong> Imagen propia</div>
                            <div className={styles.summaryItem}><strong>Técnica:</strong> Estampado</div>
                        </div>
                    </div>

                    <div className={styles.imageConfigGrid}>
                        <div className={styles.inputGroup}>
                            <label className={styles.inputLabel}>URL de imagen</label>
                            <input
                                className={styles.inputField}
                                type="url"
                                placeholder="https://..."
                                disabled={isRemovingBackground || applying}
                                value={urlValue}
                                onChange={(event) => {
                                    setUrlValue(event.target.value);
                                    setProcessedImageDataUrl("");
                                    setFileName("");
                                    setFileError("");
                                }}
                            />
                        </div>
                        <div className={styles.orDivider}>o</div>
                        <div className={styles.inputGroup}>
                            <label className={styles.inputLabel}>Subir imagen</label>
                            <input
                                type="file"
                                accept={PRINT_IMAGE_ACCEPT}
                                className={styles.fileInput}
                                disabled={isRemovingBackground || applying}
                                onChange={async (event) => {
                                    const file = event.target.files?.[0];
                                    if (!file) return;
                                    if (!isSupportedPrintImageFile(file)) {
                                        setFileError("Solo se permiten PNG, JPG, JPEG, SVG, JFIF o WEBP.");
                                        setFileName("");
                                        event.target.value = "";
                                        return;
                                    }
                                    if (file.size > MAX_PRINT_IMAGE_FILE_SIZE) {
                                        setFileError("El archivo debe pesar menos de 5 MB.");
                                        setFileName("");
                                        event.target.value = "";
                                        return;
                                    }

                                    setFileError("");
                                    setFileName(file.name || "imagen");
                                    setIsRemovingBackground(true);

                                    try {
                                        const originalDataUrl = await readBlobAsDataUrl(file);
                                        let finalDataUrl = originalDataUrl;
                                        let removalFailed = false;
                                        try {
                                            const removeBackground = await loadRemoveBackground();
                                            if (typeof removeBackground === "function") {
                                                const removedBackgroundBlob = await removeBackground(file);
                                                if (removedBackgroundBlob) {
                                                    finalDataUrl = await readBlobAsDataUrl(removedBackgroundBlob);
                                                }
                                            } else {
                                                removalFailed = true;
                                            }
                                        } catch (_error) {
                                            removalFailed = true;
                                        }

                                        setProcessedImageDataUrl(finalDataUrl);
                                        setUrlValue("");
                                        if (removalFailed) {
                                            setFileError("No se pudo quitar el fondo; se usó la imagen original.");
                                        } else {
                                            setFileError("");
                                        }
                                    } catch (_error) {
                                        setFileError("No se pudo procesar la imagen seleccionada.");
                                        setFileName("");
                                        setProcessedImageDataUrl("");
                                    } finally {
                                        setIsRemovingBackground(false);
                                        event.target.value = "";
                                    }
                                }}
                            />
                            {isRemovingBackground ? (
                                <div className={styles.printImageProcessing}>
                                    <span className={styles.printImageProcessingSpinner} aria-hidden="true" />
                                    <span>Quitando fondo de la imagen...</span>
                                </div>
                            ) : null}
                            {fileName ? <div className={styles.hint}>{`Archivo: ${fileName}`}</div> : null}
                            {fileError ? <div className={styles.summaryHint}>{fileError}</div> : null}
                        </div>
                    </div>

                    {previewSrc ? (
                        <div className={styles.littleDesignEditorPreviewWrap}>
                            <img
                                src={previewSrc}
                                alt="Preview de imagen a aplicar"
                                className={styles.littleDesignEditorPreviewImg}
                            />
                        </div>
                    ) : null}

                    {legalImageDisclaimer ? <div className={styles.hint}>{renderLittleBlueBold(legalImageDisclaimer)}</div> : null}

                    <div className={styles.modalFooter}>
                        <button type="button" className={styles.btn} disabled={applying || isRemovingBackground} onClick={onClose}>
                            Cancelar
                        </button>
                        <button
                            type="button"
                            className={`${styles.btn} ${styles.nextBtn}`}
                            disabled={!canApply}
                            onClick={() => {
                                if (processedImageDataUrl) {
                                    onApply?.(processedImageDataUrl, "file");
                                    return;
                                }
                                const trimmed = String(urlValue || "").trim();
                                if (!trimmed) return;
                                onApply?.(trimmed, "url");
                            }}
                        >
                            Aplicar
                        </button>
                    </div>
                </section>
            </div>
        </div>
    );
};
